// src/utils/firebase/friends.js
import { 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc, 
  collection, 
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase.utils';
import { serializeFirestoreData } from './serialization';
import { fetchPublicProfile } from './publicProfile';

/**
 * Adds a friend request (creates a pending friendship)
 * @param {string} userID - Current user ID
 * @param {string} friendUserID - Friend user ID to add
 * @returns {Promise<void>}
 */
export const sendFriendRequest = async (userID, friendUserID) => {
  try {
    if (userID === friendUserID) {
      throw new Error('Cannot add yourself as a friend');
    }

    // Check if friend request already exists
    const friendDocRef = doc(db, `users/${userID}/friends/${friendUserID}`);
    const friendSnap = await getDoc(friendDocRef);
    
    if (friendSnap.exists()) {
      throw new Error('Friend request already sent or user is already a friend');
    }

    // Check if the other user exists
    const friendProfile = await fetchPublicProfile(friendUserID);

    if (!friendProfile) {
      throw new Error('User not found');
    }

    // Create friend request (pending status)
    await setDoc(friendDocRef, {
      friendUID: friendUserID,
      status: 'pending',
      createdAt: serverTimestamp(),
      sentBy: userID
    });

    // Also create a reverse entry for the friend (so they can see the request)
    const reverseFriendDocRef = doc(db, `users/${friendUserID}/friends/${userID}`);
    await setDoc(reverseFriendDocRef, {
      friendUID: userID,
      status: 'pending',
      createdAt: serverTimestamp(),
      sentBy: userID
    });
  } catch (error) {
    console.error('Error sending friend request:', error);
    throw error;
  }
};

/**
 * Accepts a friend request (both users become friends)
 * @param {string} userID - Current user ID
 * @param {string} friendUserID - Friend user ID to accept
 * @returns {Promise<void>}
 */
export const acceptFriendRequest = async (userID, friendUserID) => {
  try {
    // Update both friend documents to 'accepted'
    const friendDocRef = doc(db, `users/${userID}/friends/${friendUserID}`);
    const reverseFriendDocRef = doc(db, `users/${friendUserID}/friends/${userID}`);

    await setDoc(friendDocRef, {
      friendUID: friendUserID,
      status: 'accepted',
      createdAt: serverTimestamp(),
      acceptedAt: serverTimestamp()
    }, { merge: true });

    await setDoc(reverseFriendDocRef, {
      friendUID: userID,
      status: 'accepted',
      createdAt: serverTimestamp(),
      acceptedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error accepting friend request:', error);
    throw error;
  }
};

/**
 * Removes a friend (unfriends)
 * @param {string} userID - Current user ID
 * @param {string} friendUserID - Friend user ID to remove
 * @returns {Promise<void>}
 */
export const removeFriend = async (userID, friendUserID) => {
  try {
    // Delete both friend documents
    const friendDocRef = doc(db, `users/${userID}/friends/${friendUserID}`);
    const reverseFriendDocRef = doc(db, `users/${friendUserID}/friends/${userID}`);

    await deleteDoc(friendDocRef);
    await deleteDoc(reverseFriendDocRef);
  } catch (error) {
    console.error('Error removing friend:', error);
    throw error;
  }
};

/**
 * Checks if two users are friends
 * @param {string} userID - Current user ID
 * @param {string} friendUserID - Friend user ID to check
 * @returns {Promise<boolean>} True if they are friends
 */
export const areFriends = async (userID, friendUserID) => {
  try {
    const friendDocRef = doc(db, `users/${userID}/friends/${friendUserID}`);
    const friendSnap = await getDoc(friendDocRef);
    
    if (!friendSnap.exists()) {
      return false;
    }

    const friendData = friendSnap.data();
    return friendData.status === 'accepted';
  } catch (error) {
    console.error('Error checking friendship:', error);
    return false;
  }
};

/**
 * Gets all friends for a user (accepted friendships only)
 * @param {string} userID - User ID
 * @returns {Promise<Array>} Array of friend objects with user data
 */
export const getFriends = async (userID) => {
  try {
    const friendsRef = collection(db, `users/${userID}/friends`);
    const friendsSnapshot = await getDocs(friendsRef);
    
    const friends = [];
    
    for (const friendDoc of friendsSnapshot.docs) {
      const friendData = serializeFirestoreData(friendDoc.data());
      
      // Only include accepted friendships
      if (friendData.status === 'accepted') {
        // Fetch friend's public profile
        const friendProfile = await fetchPublicProfile(friendData.friendUID);

        if (friendProfile) {
          friends.push({
            uid: friendData.friendUID,
            ...friendProfile,
            friendshipCreatedAt: friendData.createdAt,
            friendshipAcceptedAt: friendData.acceptedAt
          });
        }
      }
    }
    
    return friends;
  } catch (error) {
    console.error('Error getting friends:', error);
    throw error;
  }
};

/**
 * Gets pending friend requests (both sent and received)
 * @param {string} userID - User ID
 * @returns {Promise<Object>} Object with 'sent' and 'received' arrays
 */
export const getPendingFriendRequests = async (userID) => {
  try {
    const friendsRef = collection(db, `users/${userID}/friends`);
    const friendsSnapshot = await getDocs(friendsRef);
    
    const sent = [];
    const received = [];
    
    for (const friendDoc of friendsSnapshot.docs) {
      const friendData = serializeFirestoreData(friendDoc.data());
      
      if (friendData.status === 'pending') {
        // Fetch friend's public profile
        const friendProfile = await fetchPublicProfile(friendData.friendUID);

        if (friendProfile) {
          const requestData = {
            uid: friendData.friendUID,
            ...friendProfile,
            requestCreatedAt: friendData.createdAt
          };
          
          // sentBy is the sender's uid: if it's me, I sent it (sent); otherwise I received it (received)
          if (friendData.sentBy === userID) {
            sent.push(requestData);
          } else {
            received.push(requestData);
          }
        }
      }
    }
    
    return { sent, received };
  } catch (error) {
    console.error('Error getting pending friend requests:', error);
    throw error;
  }
};

/**
 * Finds a user by username
 * @param {string} username - Username to search for
 * @returns {Promise<Object|null>} User data or null if not found
 */
export const findUserByUsername = async (username) => {
  try {
    const usernameDocRef = doc(db, `usernames/${username}`);
    const usernameSnap = await getDoc(usernameDocRef);
    
    if (!usernameSnap.exists()) {
      return null;
    }
    
    const usernameData = usernameSnap.data();
    const userProfile = await fetchPublicProfile(usernameData.uid);

    if (!userProfile) {
      return null;
    }
    
    return {
      ...userProfile,
      uid: usernameData.uid,
      username: username
    };
  } catch (error) {
    console.error('Error finding user by username:', error);
    throw error;
  }
};

